import React, { useEffect, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { SearchIcon } from 'components/Common/SvgIcons'
import DirectoryList from 'components/Clients/DirectoryList'
import { TopActiveClientsContainer } from 'pages/Clients/container'
import GrowingContainer from 'components/Common/GrowingContainer'
import { ClientsWrapper, GrowingContainerWrapper, TextWrapper } from 'components/Clients/style'
import { getFilteredList } from './helper'
import Button from 'components/Common/Button'
import useNavigateTo from 'hooks/useNavigateTo'
import redirectTo from 'utils/redirectTo'
import { getTenantId } from 'utils/getTenantId'
import { useFeatureFlag } from 'hooks/userFeatureFlag'
import { getRoleFromPath } from 'utils/helper'

export interface IClient {
  id: string
  status: string
  email: string
  firstName: string
  lastName: string
  createdAt: string
  industry?: string
  updatedAt: string
  businessName?: string
}

function compareClients(a: IClient, b: IClient): number {
  const firstNameComparison = a?.firstName?.localeCompare(b.firstName)
  return firstNameComparison ? firstNameComparison : a?.lastName?.localeCompare(b.lastName)
}

export default function Clients({
  userId,
  clientList,
  clientData,
  isAllClientFetching,
  getAdvisorDashboardAction,
  isAdvisorDashboardLoading,
}) {
  const { t } = useTranslation()
  const userRole = getRoleFromPath()
  const tenantId = getTenantId()

  const isV2FeatureFlagOrgEnabled = useFeatureFlag(userId, 'release-v2-org')
  const isV2FeatureFlagUserEnabled = useFeatureFlag(userId, 'release-v2-user')
  const isV2FlagEnabled = isV2FeatureFlagOrgEnabled && isV2FeatureFlagUserEnabled

  const [filteredList, setFilteredList] = useState<any>(undefined)
  useEffect(() => {
    getAdvisorDashboardAction()
  }, [getAdvisorDashboardAction])

  const setClientsList = (array: IClient[]) => {
    setFilteredList(array.slice().sort(compareClients))
  }

  useEffect(() => {
    if (clientList && clientList.length > 0) {
      setClientsList(clientList)
    } else {
      setClientsList([])
    }
  }, [clientList])

  const alphabetListData = useMemo(
    () => filteredList?.map((item) => (item.firstName || '') + item.id),
    [filteredList]
  )
  const navigate = useNavigateTo('createBusinessOwner', '?returnTo=clients')
  return (
    <ClientsWrapper>
      {/* Mobile Layout (below sm breakpoint) */}
      <div className="sm:hidden w-full bg-white min-h-screen">
        <GrowingContainer className="px-4 pt-6 pb-8">
          {/* Header Section */}
          <div className="mb-4">
            <h1 className="text-black-light text-2xl font-medium font-primary m-0 mb-2">
              {t('clients.myClients')}
            </h1>
            {/* <p className="text-sm font-normal text-silver font-primary mb-1">
              Top 3 Active Clients
            </p>
            <p className="text-xs font-normal text-silver font-primary mb-4">
              Based on activity on the platform
            </p> */}
            <Button
              type="button"
              label={t('clients.addNewClient')}
              variant="primary"
              onClick={() => {
                if (isV2FlagEnabled) {
                  redirectTo(
                    `/${userRole + '-v2'}/${tenantId}/create-business-owner?returnTo=clients`
                  )
                } else {
                  navigate()
                }
              }}
              className="w-full !rounded-lg"
              size="small"
            />
          </div>

          {/* Client Directory Section */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-black-light text-lg font-medium font-primary m-0">
                {t('clients.clientDirectory')}
              </h2>
              <div className="flex items-center gap-1.5">
                <span className="text-base font-normal text-silver font-primary">
                  {t('clients.currentClients')}
                </span>
                <span className="text-lg font-semibold text-black-light font-primary">
                  {clientList?.length}
                </span>
              </div>
            </div>
            <div className="relative w-full mb-6">
              <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 fill-silver w-4 h-4 pointer-events-none" />
              <input
                type="search"
                placeholder={t('clients.searchPlaceholder')}
                onChange={(e) => {
                  let list = getFilteredList(e.target.value, clientList)
                  setClientsList(list || [])
                }}
                className="w-full pl-10 pr-4 py-2.5 bg-white rounded-lg border border-grey-lighter text-sm font-primary placeholder-silver focus:outline-none focus:border-primary"
              />
            </div>
          </div>

          {/* Top 3 Active Clients Section */}
          <div>
            <h3 className="text-black-light text-base font-medium font-primary mb-2">
              {t('clients.top3ActiveClients')}
            </h3>
            <p className="text-base font-normal text-silver font-primary mb-4">
              {t('clients.basedOnActivity')}
            </p>
            <TopActiveClientsContainer />
          </div>
        </GrowingContainer>
      </div>

      {/* Tablet/iPad Layout (sm to mdl breakpoint) */}
      <div className="hidden sm:block mdl:hidden w-full bg-white min-h-screen">
        <GrowingContainer className="px-6 pt-6 pb-8">
          {/* Header Section */}
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-black-light text-3xl font-medium font-primary m-0">
              {t('clients.myClients')}
            </h1>
            <Button
              type="button"
              label={t('clients.addNewClient')}
              variant="primary"
              onClick={() => {
                if (isV2FlagEnabled) {
                  redirectTo(
                    `/${userRole + '-v2'}/${tenantId}/create-business-owner?returnTo=clients`
                  )
                } else {
                  navigate()
                }
              }}
              className="whitespace-nowrap flex-shrink-0 !rounded-lg"
              size="small"
            />
          </div>

          {/* Client Directory Section */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-black-light text-xl font-medium font-primary m-0">
                {t('clients.clientDirectory')}
              </h2>
              <div className="flex items-center gap-2">
                <span className="text-lg font-normal text-silver font-primary">
                  {t('clients.currentClients')}
                </span>
                <span className="text-2xl font-semibold text-black-light font-primary">
                  {clientList?.length}
                </span>
              </div>
            </div>
            <div className="relative w-full">
              <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 fill-silver w-4 h-4 pointer-events-none" />
              <input
                type="search"
                placeholder={t('clients.searchPlaceholder')}
                onChange={(e) => {
                  let list = getFilteredList(e.target.value, clientList)
                  setClientsList(list || [])
                }}
                className="w-full pl-10 pr-4 py-2.5 bg-white rounded-lg border border-grey-lighter text-base font-primary placeholder-silver focus:outline-none focus:border-primary"
              />
            </div>
          </div>

          {/* Top 3 Active Clients Section */}
          <div>
            <h3 className="text-black-light text-lg font-medium font-primary mb-2">
              {t('clients.top3ActiveClients')}
            </h3>
            <p className="text-lg font-normal text-silver font-primary mb-4">
              {t('clients.basedOnActivity')}
            </p>
            <TopActiveClientsContainer />
          </div>
        </GrowingContainer>
      </div>

      {/* Desktop Layout */}
      <GrowingContainerWrapper className="mdl:block hidden">
        <GrowingContainer className="sm:px-2 mdl:pr-4 lg:pr-4 pt-8">
          <div>
            <div className="mb-8 sm:mb-4">
              <div className="flex items-start justify-between sm:space-y-0 gap-4">
                <div className="flex flex-col">
                  <TextWrapper className="text-[44px] sm:text-3xl font-bold text-black-light mb-2">
                    {t('clients.myClients')}
                  </TextWrapper>
                  <TextWrapper className="text-xl sm:text-xl font-semibold text-black mb-1">
                    {t('clients.top3ActiveClients')}
                  </TextWrapper>
                  <p className="font-primary text-base font-normal text-silver">
                    {t('clients.basedOnActivity')}
                  </p>
                </div>
                <Button
                  type="button"
                  label={t('clients.addNewClient')}
                  variant="primary"
                  onClick={() => {
                    if (isV2FlagEnabled) {
                      redirectTo(
                        `/${userRole + '-v2'}/${tenantId}/create-business-owner?returnTo=clients`
                      )
                    } else {
                      navigate()
                    }
                  }}
                  size="small"
                  className="
    !rounded-md
    !bg-[#6C3EF2]
    !text-white
    !px-5
    !py-2.5
    !h-10
    !font-medium
    !shadow-sm
    hover:!bg-[#5A2DE3]
    active:!bg-[#4A21C7]
  "
                />
              </div>
            </div>
          </div>

          <TopActiveClientsContainer />
        </GrowingContainer>
      </GrowingContainerWrapper>

      {!isAllClientFetching && (
        <div
          id="client-directory"
          className="mdl:block hidden xs:col-span-12 sm:col-span-6 md:col-span-6 lg:col-span-3 xl:col-span-3 xxl:col-span-3 border-l border-secondary-text mdl:col-span-3 bg-white"
        >
          <div className="px-6 pt-8 pb-4">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-black text-lg font-medium font-primary m-0">
                {t('clients.clientDirectory')}
              </h2>
              <div className="flex items-center gap-2">
                <span className="text-lg font-normal text-silver font-primary">
                  {t('clients.currentClients')}
                </span>
                <span className="text-xl font-semibold text-black-light font-primary">
                  {clientList?.length}
                </span>
              </div>
            </div>
            <div className="relative w-full">
              <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 fill-silver w-4 h-4 pointer-events-none" />
              <input
                type="search"
                placeholder={t('clients.searchPlaceholder')}
                onChange={(e) => {
                  let list = getFilteredList(e.target.value, clientList)
                  setClientsList(list || [])
                }}
                className="w-full pl-10 pr-4 py-2.5 bg-white rounded-lg border border-grey-lighter text-base font-primary placeholder-silver focus:outline-none focus:border-primary"
              />
            </div>
          </div>
          <div className="relative">
            <DirectoryList
              alphabetListData={alphabetListData}
              filteredList={filteredList}
              clientList={clientList}
            />
          </div>
        </div>
      )}
    </ClientsWrapper>
  )
}
